import React from "react";
import { Popover, Button, Typography, Box, Radio, RadioGroup, FormControlLabel, Divider } from "@mui/material";
import { ArrowDownAZ, ArrowUpAZ } from "lucide-react";
import { FilterParams } from "../transactions-table";

export interface CategoryOption {
    id: string;
    label: string;
    children?: CategoryOption[];
}

export type CategoryFilterHandle = {
    applyNow: () => void;
    clear: () => void;
};

export interface CategoryFilterProps {
    anchorEl?: HTMLElement | null;
    open?: boolean;
    onApply: (params: FilterParams) => React.SetStateAction<void>;
    onClose?: () => void;
    currentSort?: {
        sortBy?: string;
        sortDirection?: 'ASC' | 'DESC';
    };
    categoryOptions?: CategoryOption[];
    // Renders the same sort controls inline (no Popover/anchor) for use
    // inside a combined modal instead of an anchored popup.
    embedded?: boolean;
    // Hides this section's own Apply Now/Clear buttons - used when a
    // parent (the combined filter modal) drives Apply/Clear for every
    // section at once via the imperative handle below instead.
    hideActions?: boolean;
}

const CategoryFilter = React.forwardRef<CategoryFilterHandle, CategoryFilterProps>(({
    anchorEl,
    open,
    onApply,
    onClose,
    currentSort,
    categoryOptions = [],
    embedded = false,
    hideActions = false,
}, ref) => {
    const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc' | ''>(
        currentSort?.sortBy === 'category' ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : ''
    );

    // Update state when props change
    React.useEffect(() => {
        setSortDirection(currentSort?.sortBy === 'category' ? (currentSort?.sortDirection === 'ASC' ? 'asc' : 'desc') : '');
    }, [currentSort]);

    const handleClear = () => {
        setSortDirection('');
        // Clear category filter but default sorting to date
        onApply({
            sortBy: 'date',
            sortDirection: 'DESC',
        });
    };

    const handleApplyNow = () => {
        const params: any = {};
        if (sortDirection) {
            params.sortBy = 'category';
            params.sortDirection = sortDirection.toUpperCase() as 'ASC' | 'DESC';
        }
        onApply(params);
    };

    React.useImperativeHandle(ref, () => ({
        applyNow: handleApplyNow,
        clear: handleClear,
    }));

    const content = (
        <>
            {!embedded && <>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Sort</Typography>
                <Divider sx={{ my: 1 }}/>
            </>}
            <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography fontSize={14}>Sort By Category</Typography>
            </Box>
            <RadioGroup value={sortDirection || ''} onChange={e => setSortDirection(e.target.value as 'asc' | 'desc')} sx={{ mb: 1, pl: 3 }}>
                <FormControlLabel 
                    value="asc" 
                    sx={{ height: 24 }}
                    control={<Radio size='small'/>} 
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowDownAZ size={14} />Sort A to Z</Typography>} 
                />
                <FormControlLabel 
                    value="desc" 
                    sx={{ height: 24 }}
                    control={<Radio size='small'/>} 
                    label={<Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 1 }}><ArrowUpAZ size={14} />Sort Z to A</Typography>} 
                />
            </RadioGroup>
            {!hideActions && (
                <Box display="flex" gap={1} mt={2}>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-primary)' } }}
                        onClick={handleApplyNow}
                    >
                        Apply Now
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-container-background)' } }}
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                </Box>
            )}
        </>
    );

    if (embedded) {
        return <Box>{content}</Box>;
    }

    return (
        <Popover
            open={!!open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{
                sx: {
                borderRadius: 2,
                boxShadow: 3,
                p: 2,
                minWidth: 300,
                },
            }}
        >
            {content}
        </Popover>
    );
});

export default CategoryFilter;
